import React, { useState } from "react";
import ReactModal from "react-modal";
import styles from "./DownloadModal.module.css";
import { ROUTES } from "../../constants/routes";

// Set app element for accessibility
if (typeof window !== "undefined") {
  ReactModal.setAppElement("body");
}

interface DownloadModalProps {
  variant?: "button" | "banner" | "inline";
  className?: string;
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  variant = "button",
  className = "",
}) => {
  const [qrCodeLoaded, setQrCodeLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const playStoreURL = ROUTES.EXTERNAL.GOOGLE_PLAY;
  const appStoreURL = ROUTES.EXTERNAL.APP_STORE;
  const webAppURL = ROUTES.HOME.path;

  const appFeatures = [
    {
      icon: "fas fa-user-md",
      title: "Doctor Consultations",
      description: "Connect with 800+ certified healthcare providers",
      color: "blue",
    },
    {
      icon: "fas fa-coins",
      title: "Fee Cashback Rewards",
      description: "Earn up to 50% cashback on transaction fees",
      color: "orange",
    },
    {
      icon: "fas fa-heart",
      title: "Community Support",
      description: "Donate cashback or request aid from the community",
      color: "red",
    },
    {
      icon: "fas fa-shield-alt",
      title: "Secure Platform",
      description: "Military-grade encryption for all health data",
      color: "green",
    },
    {
      icon: "fas fa-clock",
      title: "24/7 Available",
      description: "Round-the-clock mobile healthcare support",
      color: "purple",
    },
  ];

  const handleDownload = (platform: string) => {
    const urls = {
      ios: appStoreURL,
      android: playStoreURL,
      web: webAppURL,
    };

    window.open(urls[platform as keyof typeof urls], "_blank");
    setShowModal(false);
  };

  const generateQRCode = (url: string) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
      url
    )}&bgcolor=f0f9ff&color=ff6b35&margin=1&qzone=2`;
  };

  const openModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const TriggerButton = () => (
    <button
      className={`${styles.downloadTrigger} ${className}`}
      onClick={openModal}
      aria-label={`Download ${ROUTES.COMPANY.NAME} Mobile App`}
      type="button"
    >
      <i className={`fas fa-download ${styles.triggerIcon}`}></i>
      <span className={styles.triggerText}>Download App</span>
      <div className={styles.triggerBadge}>Free</div>
    </button>
  );

  const BannerVariant = () => (
    <div className={styles.downloadBanner}>
      <div className={styles.bannerContent}>
        <div className={styles.bannerIcon}>
          <i className="fas fa-mobile-alt"></i>
        </div>
        <div className={styles.bannerText}>
          <h4>Get the {ROUTES.COMPANY.NAME} App</h4>
          <p>Healthcare with fee cashback rewards on your mobile device</p>
        </div>
        <button
          className={styles.bannerButton}
          onClick={openModal}
          type="button"
        >
          Download
        </button>
      </div>
    </div>
  );

  if (variant === "banner") return <BannerVariant />;
  if (variant === "inline") return <TriggerButton />;

  return (
    <>
      <TriggerButton />

      <ReactModal
        isOpen={showModal}
        onRequestClose={closeModal}
        contentLabel={`Download ${ROUTES.COMPANY.NAME} App`}
        className={styles.modalContent}
        overlayClassName={styles.modalOverlay}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        ariaHideApp={true}
      >
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.appIcon}>
              <i className="fas fa-mobile-alt"></i>
            </div>
            <div className={styles.headerText}>
              <h2 className={styles.modalTitle}>
                Download {ROUTES.COMPANY.NAME}
              </h2>
              <p className={styles.modalSubtitle}>
                Mobile healthcare with fee cashback rewards - Free & Secure
              </p>
            </div>
          </div>
          <button
            className={styles.closeButton}
            onClick={closeModal}
            aria-label="Close modal"
            type="button"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          <div className={styles.bodyGrid}>
            {/* App Features */}
            <div className={styles.featuresSection}>
              <h3 className={styles.sectionTitle}>
                Why Choose {ROUTES.COMPANY.NAME}?
              </h3>
              <div className={styles.featuresList}>
                {appFeatures.map((feature, index) => (
                  <div
                    key={index}
                    className={`${styles.featureItem} ${
                      styles[`feature${feature.color}`]
                    }`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div
                      className={`${styles.featureIcon} ${
                        styles[`icon${feature.color}`]
                      }`}
                    >
                      <i className={feature.icon}></i>
                    </div>
                    <div className={styles.featureContent}>
                      <h4 className={styles.featureTitle}>{feature.title}</h4>
                      <p className={styles.featureDescription}>
                        {feature.description}
                      </p>
                    </div>
                    <div className={styles.featureCheck}>
                      <i className="fas fa-check-circle"></i>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Download Options */}
            <div className={styles.downloadSection}>
              <h3 className={styles.sectionTitle}>Available Platforms</h3>

              {/* Download Buttons */}
              <div className={styles.downloadButtons}>
                <button
                  className={`${styles.downloadBtn} ${styles.playStoreBtn}`}
                  onClick={() => handleDownload("android")}
                  type="button"
                >
                  <i className={`fab fa-google-play ${styles.btnIcon}`}></i>
                  <div className={styles.btnText}>
                    <small>Get it on</small>
                    <span>Google Play</span>
                  </div>
                  <div className={styles.btnBadge}>Free</div>
                </button>

                <button
                  className={`${styles.downloadBtn} ${styles.appStoreBtn}`}
                  onClick={() => handleDownload("ios")}
                  type="button"
                >
                  <i className={`fab fa-apple ${styles.btnIcon}`}></i>
                  <div className={styles.btnText}>
                    <small>Download on the</small>
                    <span>App Store</span>
                  </div>
                  <div className={styles.btnBadge}>Free</div>
                </button>

                <button
                  className={`${styles.downloadBtn} ${styles.webAppBtn}`}
                  onClick={() => handleDownload("web")}
                  type="button"
                >
                  <i className={`fas fa-globe ${styles.btnIcon}`}></i>
                  <div className={styles.btnText}>
                    <small>Open in</small>
                    <span>Web Browser</span>
                  </div>
                  <div className={styles.btnBadge}>PWA</div>
                </button>
              </div>

              {/* QR Code Section */}
              <div className={styles.qrSection}>
                <h4 className={styles.qrTitle}>
                  <i className={`fas fa-qrcode ${styles.qrIcon}`}></i>
                  Scan to Download
                </h4>
                <div className={styles.qrContainer}>
                  <div className={styles.qrCodeWrapper}>
                    {!qrCodeLoaded && (
                      <div className={styles.qrPlaceholder}>
                        <i className="fas fa-qrcode"></i>
                        <span>Loading QR Code...</span>
                      </div>
                    )}
                    <img
                      src={generateQRCode(playStoreURL)}
                      alt={`QR Code for ${ROUTES.COMPANY.NAME} App Download`}
                      className={styles.qrCode}
                      onLoad={() => setQrCodeLoaded(true)}
                      onError={() => setQrCodeLoaded(false)}
                      style={{ display: qrCodeLoaded ? "block" : "none" }}
                    />
                  </div>
                  <p className={styles.qrDescription}>
                    Point your phone camera at the QR code to download instantly
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <div className={styles.footerContent}>
            <div className={styles.securityRow}>
              <div className={styles.securityBadge}>
                <i className="fas fa-shield-alt"></i>
                <span>Secure Platform</span>
              </div>
              <div className={styles.securityBadge}>
                <i className="fas fa-coins"></i>
                <span>Fee Cashback</span>
              </div>
            </div>
            <p className={styles.footerNote}>
              Free download • No subscription required
            </p>
            <p className={styles.footerSubNote}>
              Mobile healthcare with transparent fees
            </p>
          </div>
        </div>
      </ReactModal>
    </>
  );
};

export default DownloadModal;
